import { Box, Paper, useTheme } from "@mui/material";
import React, { memo, useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  LineProps,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from "recharts";

import { Typography } from "new-components/typography/Typography";

const CustomTooltip = memo(
  (
    props: TooltipProps<number, string> & {
      color: string;
      tooltipTitle?: React.ReactNode | string;
    }
  ): JSX.Element | null => {
    const { active, payload, color, tooltipTitle } = props;
    if (active && payload && payload.length) {
      const point = payload[0].payload;
      const { y } = point;
      return (
        <Paper sx={{ bgcolor: color, px: 1, py: 0.5 }} elevation={4}>
          <Box display="flex" alignItems="center" color="white" textAlign="start">
            <Typography variant="subtitle" fontWeight="bold" mr={1}>
              {(y * 100 > 99.99 ? 99.99 : y * 100)?.toFixed(2)}%
            </Typography>
            <Typography fontWeight="medium">
              {tooltipTitle}
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  }
);

type ConfidenceLineChartProps = {
  data?: { x: number; y: number }[];
  xTicks: number[];
  yTicks?: number[];
  color?: string;
  syncId?: string;
  tooltipTitle?: React.ReactNode | string;
  tooltipPositionIndex?: number;
  tooltipOptions?: TooltipProps<number, string>;
  axisOptions?: {
    x?: XAxisProps;
    y?: YAxisProps;
  };
  lineOptions?: {
    dashedLine?: Omit<LineProps, "ref">;
    solidLine?: Omit<LineProps, "ref">;
  };
  horizontalLines?: number;
  showDashedLine?: boolean;
  domain?: [number, number];
};

const DEFAULT_TICKS = [0, 0.25, 0.5, 0.75, 1];

const getHorizontalLines = (horizontalLines: number, from: number, to: number) => {
  const lines = [];
  for (let i = 0; i < horizontalLines; i += 1) {
    lines.push(from + (to - from) * (i / (horizontalLines - 1)));
  }
  return lines;
};

export const ConfidenceLineChart = React.memo(
  ({
    data,
    xTicks = DEFAULT_TICKS,
    yTicks = DEFAULT_TICKS,
    color,
    syncId,
    tooltipTitle,
    tooltipPositionIndex = 0,
    tooltipOptions,
    axisOptions,
    lineOptions,
    horizontalLines,
    showDashedLine = true,
    domain,
  }: ConfidenceLineChartProps) => {
    const {
      palette: { info, primary },
    } = useTheme();

    const [strokeDasharray, setStrokeDasharray] = useState("2");
    const [strokeLinecap, setStrokeLinecap] = useState<LineProps["strokeLinecap"]>("round");

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const [_width, setWidth] = useState<number | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const [_height, setHeight] = useState<number | null>(null);

    return (
      <ResponsiveContainer
        width="100%"
        height={120}
        onResize={(w, h) => {
          // States update on resize to force update of the rendered tooltip
          // position, without this the tooltip goes to index 0 on resize.
          setWidth(w);
          setHeight(h);

          if (!w && !h) {
            return;
          }

          if (w > 508) {
            setStrokeDasharray("5 5");
            setStrokeLinecap("round");
          } else {
            setStrokeDasharray("2 2");
            setStrokeLinecap(undefined);
          }
        }}
      >
        <ComposedChart
          data={data}
          style={{ fontSize: "0.75rem", pointerEvents: "none" }}
          syncId={syncId}
          margin={{ top: 4, bottom: -8 }}
        >
          <XAxis
            type="number"
            domain={
              domain || (xTicks.length > 0 ? [xTicks[0], xTicks[xTicks.length - 1]] : DEFAULT_TICKS)
            }
            ticks={xTicks.length > 0 ? xTicks : DEFAULT_TICKS}
            dataKey="x"
            tickLine={false}
            tickMargin={2}
            {...axisOptions?.x}
          />
          <YAxis
            type="number"
            domain={[yTicks[0], yTicks[yTicks.length - 1]]}
            dataKey="y"
            ticks={yTicks}
            tickFormatter={(tick: number) => `${(tick * 100).toFixed(0)}%`}
            tickLine={false}
            tickMargin={1}
            interval={0}
            {...axisOptions?.y}
          />
          <CartesianGrid
            horizontal
            horizontalValues={
              horizontalLines ? getHorizontalLines(horizontalLines || 0, yTicks[0], yTicks[yTicks.length - 1]) : yTicks
            }
          />
          <Tooltip
            content={<CustomTooltip color={color || info.main} tooltipTitle={tooltipTitle} />}
            active
            defaultIndex={data && data.length > 0 ? tooltipPositionIndex : undefined}
            trigger="click"
            allowEscapeViewBox={{ y: true }}
            offset={-15}
            cursor={{
              strokeWidth: data && data.length > 0 ? 5 : 0,
              stroke: primary.main,
            }}
            isAnimationActive={false}
            {...tooltipOptions}
          />

          <Line
            type="monotone"
            dataKey="y"
            stroke={color || info.main}
            fill={color || info.main}
            strokeWidth={5}
            dot={false}
            strokeOpacity={showDashedLine ? 0.25 : 1}
            {...lineOptions?.solidLine}
          />
          {showDashedLine && (
            <Line
              type="monotone"
              dataKey="y"
              stroke={color || info.main}
              strokeWidth={2.5}
              dot={false}
              strokeDasharray={strokeDasharray}
              strokeLinecap={strokeLinecap}
              activeDot={{ r: 8, fill: color || info.main }}
              {...lineOptions?.dashedLine}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }
);
